import * as THREE from 'three'

/**
 * Sun-tinted aerial perspective.
 *
 * Three's fog mixes toward one flat colour regardless of where you are looking,
 * which is exactly wrong outdoors: haze is lit *by* the sun, so it glows warm
 * when you look toward it and goes cold blue when you look away. On a snow
 * scene that directional shift is doing most of the work of separating a ridge
 * two hundred metres away from one eight hundred metres away — everything is
 * white, so distance has to be carried by hue rather than by tone.
 *
 * Shared uniforms so the terrain and the scenery fog identically; two surfaces
 * hazing differently at the same distance is instantly visible where trees meet
 * snow.
 */
export const HAZE = {
  sunDir: { value: new THREE.Vector3(0, 1, 0) },
  warm: { value: new THREE.Color(0xf2ecdc) },
  cool: { value: new THREE.Color(0xb9cfe4) },
}

export function setHazeSun(dir: THREE.Vector3): void {
  HAZE.sunDir.value.copy(dir).normalize()
}

/**
 * Patch a material's fog to be directional. Call inside onBeforeCompile, or use
 * `withSunHaze` to wrap a material that has no other injection.
 */
export function injectSunHaze(shader: {
  vertexShader: string
  fragmentShader: string
  uniforms: Record<string, THREE.IUniform>
}): void {
  shader.uniforms.uSunDir = HAZE.sunDir
  shader.uniforms.uHazeWarm = HAZE.warm
  shader.uniforms.uHazeCool = HAZE.cool

  // The world position varying may already exist (the snow material adds one);
  // guard so we never declare it twice.
  if (!shader.vertexShader.includes('vHazePos')) {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vHazePos;')
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\nvHazePos = (modelMatrix * vec4(transformed, 1.0)).xyz;',
      )
  }

  shader.fragmentShader = shader.fragmentShader
    .replace(
      '#include <common>',
      `#include <common>
       varying vec3 vHazePos;
       uniform vec3 uSunDir;
       uniform vec3 uHazeWarm;
       uniform vec3 uHazeCool;`,
    )
    .replace(
      '#include <fog_fragment>',
      `#ifdef USE_FOG
         float hazeFactor = smoothstep( fogNear, fogFar, vFogDepth );
         vec3 viewW = normalize( vHazePos - cameraPosition );
         float towardSun = max( dot( viewW, uSunDir ), 0.0 );
         vec3 hazeCol = mix( uHazeCool, uHazeWarm, pow( towardSun, 2.5 ) );
         gl_FragColor.rgb = mix( gl_FragColor.rgb, hazeCol, hazeFactor );
       #endif`,
    )
}

/** Wrap a plain material so its fog becomes directional. */
export function withSunHaze<T extends THREE.Material>(mat: T, cacheKey: string): T {
  const prev = mat.onBeforeCompile?.bind(mat)
  mat.onBeforeCompile = (shader, renderer) => {
    prev?.(shader, renderer)
    injectSunHaze(shader as unknown as Parameters<typeof injectSunHaze>[0])
  }
  mat.customProgramCacheKey = () => cacheKey
  return mat
}
